"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.listQuerySchema = exports.endCourierClientIdSchema = exports.courierClientIdSchema = exports.moveCourierSchema = exports.updateCourierSchema = exports.createCourierSchema = exports.teamSchema = exports.hubSchema = exports.merchantSchema = exports.projectSchema = exports.COURIER_STATUSES = exports.VEHICLE_TYPES = exports.COD_CYCLES = void 0;
// Setup data: projects, merchants, hubs, teams, couriers (plan 4.1).
const zod_1 = require("zod");
const phone_1 = require("./phone");
const code = zod_1.z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9_-]{1,23}$/, 'CODE_FORMAT');
const name = zod_1.z.string().trim().min(2).max(120);
const reason = zod_1.z.string().trim().max(500).optional();
const optionalMobile = zod_1.z
    .string()
    .trim()
    .transform((v, ctx) => {
    if (v === '')
        return null;
    const n = (0, phone_1.normalizeUaeMobile)(v);
    if (!n) {
        ctx.addIssue({ code: 'custom', message: 'INVALID_PHONE' });
        return zod_1.z.NEVER;
    }
    return n;
})
    .nullish();
exports.COD_CYCLES = ['DAILY', 'TWICE_WEEKLY', 'WEEKLY', 'BIWEEKLY', 'MONTHLY'];
exports.VEHICLE_TYPES = ['MOTORBIKE', 'CAR', 'VAN', 'BICYCLE', 'ON_FOOT'];
exports.COURIER_STATUSES = ['ACTIVE', 'ON_LEAVE', 'SUSPENDED', 'LEFT'];
exports.projectSchema = zod_1.z.object({ code, name, isActive: zod_1.z.boolean().default(true), receiveOnImport: zod_1.z.boolean().optional(), reason });
exports.merchantSchema = zod_1.z.object({
    projectId: zod_1.z.uuid(),
    code,
    name,
    isActive: zod_1.z.boolean().default(true),
    /** plan 5.8: inscan within 2 h of hub arrival */
    inscanSlaHours: zod_1.z.coerce.number().int().min(1).max(72).default(2),
    /** plan 5.8: first out-for-delivery within 24 h of hub arrival */
    firstOfdSlaHours: zod_1.z.coerce.number().int().min(1).max(168).default(24),
    codCycle: zod_1.z.enum(exports.COD_CYCLES).default('DAILY'),
    /** plan 5.4: partial delivery only if the merchant allows it */
    allowPartialDelivery: zod_1.z.boolean().default(false),
    notes: zod_1.z.string().trim().max(1000).nullish(),
    /** D-0012: other names client files use for this merchant (optional, never defaulted, so an edit keeps them) */
    aliases: zod_1.z.array(zod_1.z.string().trim().min(1).max(200)).max(50).optional(),
    reason,
});
exports.hubSchema = zod_1.z.object({
    code,
    name,
    address: zod_1.z.string().trim().max(300).nullish(),
    latitude: zod_1.z.coerce.number().min(22).max(27).nullish(),
    longitude: zod_1.z.coerce.number().min(51).max(57).nullish(),
    isActive: zod_1.z.boolean().default(true),
    reason,
});
exports.teamSchema = zod_1.z.object({
    code,
    name,
    hubId: zod_1.z.uuid(),
    isActive: zod_1.z.boolean().default(true),
    reason,
});
const courierFields = {
    /** the Blink ID (D-0020); a trial driver starts with e.g. TRIAL-01 and gets the real one later */
    employeeCode: code,
    companyPhone: optionalMobile,
    vehicleType: zod_1.z.enum(exports.VEHICLE_TYPES).default('MOTORBIKE'),
    vehiclePlate: zod_1.z.string().trim().toUpperCase().max(20).nullish(),
    capacityParcels: zod_1.z.coerce.number().int().min(1).max(500).default(120),
    homeArea: zod_1.z.string().trim().max(120).nullish(),
    deviceInfo: zod_1.z.string().trim().max(200).nullish(),
};
/** New courier = sign-in account + courier profile + courier role at the home hub (D-0020). */
exports.createCourierSchema = zod_1.z.object({
    fullName: name,
    phone: zod_1.z
        .string()
        .trim()
        .transform((v, ctx) => {
        const n = (0, phone_1.normalizeUaeMobile)(v);
        if (!n) {
            ctx.addIssue({ code: 'custom', message: 'INVALID_PHONE' });
            return zod_1.z.NEVER;
        }
        return n;
    }),
    email: zod_1.z.string().trim().toLowerCase().pipe(zod_1.z.email()).nullish().or(zod_1.z.literal('').transform(() => null)),
    hubId: zod_1.z.uuid(),
    ...courierFields,
});
exports.updateCourierSchema = zod_1.z.object({
    employeeCode: courierFields.employeeCode.optional(),
    companyPhone: courierFields.companyPhone,
    vehicleType: courierFields.vehicleType.optional(),
    vehiclePlate: courierFields.vehiclePlate,
    capacityParcels: courierFields.capacityParcels.optional(),
    homeArea: courierFields.homeArea,
    deviceInfo: courierFields.deviceInfo,
    status: zod_1.z.enum(exports.COURIER_STATUSES).optional(),
    reason,
});
/** Change the courier's home hub (D-0020; replaces moving between teams). */
exports.moveCourierSchema = zod_1.z.object({
    hubId: zod_1.z.uuid(),
    reason: zod_1.z.string().trim().min(3).max(500),
});
const isoDay = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'DATE_FORMAT');
/**
 * The courier's ID in a client project (D-0020), held from a date. Giving an ID that another
 * courier holds ends theirs the day before; the courier's earlier ID in that project ends too.
 */
exports.courierClientIdSchema = zod_1.z.object({
    projectId: zod_1.z.uuid(),
    clientId: zod_1.z.string().trim().toUpperCase().min(1).max(40).regex(/^[A-Z0-9][A-Z0-9 ._/-]*$/, 'CLIENT_ID_FORMAT'),
    validFrom: isoDay,
    reason,
});
/** Ends a client ID (last day held). */
exports.endCourierClientIdSchema = zod_1.z.object({ validTo: isoDay, reason });
exports.listQuerySchema = zod_1.z.object({
    search: zod_1.z.string().trim().max(100).optional(),
    hubId: zod_1.z.uuid().optional(),
    teamId: zod_1.z.uuid().optional(),
    projectId: zod_1.z.uuid().optional(),
    status: zod_1.z.string().trim().max(20).optional(),
    page: zod_1.z.coerce.number().int().min(1).default(1),
    pageSize: zod_1.z.coerce.number().int().min(1).max(200).default(50),
});
//# sourceMappingURL=org.js.map